import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import classes from 'classnames';
import classNames from './Camera.module.scss';
import { useTranslation } from 'react-i18next';
import CameraController from '@/components/ConversationWithTutor/CameraController/CameraController';
import { Transition } from 'react-transition-group';
import TheButton from '@/components/TheButton/TheButton';
import AppBackdrop from '@/components/AppBackdrop';
import AppModal from '@/components/AppModal';
import commonUtils from '@/utils/common';
import SVG from 'react-inlinesvg';
import cameraIcon from '@/assets/svg/camera.svg';

enum CameraErrorTypes {
  none = 'none',
  permissions = 'permissions',
  device_not_found = 'deviceNotFound',
  device_in_use = 'deviceInUse',
  failed_to_access_the_device = 'failedToAccessTheDevice',
}

interface CameraProps {
  className?: string;
  permissionClassName?: string;
  aspectRatio?: number;
  isOn: boolean;
  toggleCamera?: () => void;
}

const Camera = ({
  className,
  permissionClassName,
  aspectRatio,
  isOn,
  toggleCamera,
  ...props
}: React.DOMAttributes<HTMLDivElement> & CameraProps) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [permissionGranted, setPermissionGranted] = useState<boolean | null>(null);
  const [showPermissionPopup, setShowPermissionPopup] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<CameraErrorTypes>(CameraErrorTypes.none);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach(track => track.stop());
      } catch (err) {
        console.error('Error stoping camera:', err);
      }

      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();

    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          aspectRatio: aspectRatio,
        },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = streamRef.current;
        await videoRef.current.play();
        setPermissionGranted(true);
        setShowPermissionPopup(false);
        setErrorType(CameraErrorTypes.none);
      }
    } catch (error) {
      streamRef.current = null;
      console.error('Error accessing camera:', error);
      setPermissionGranted(false);
      setShowPermissionPopup(true);

      if (error) {
        switch ((error as any).name) {
          case 'NotAllowedError':
            setErrorType(CameraErrorTypes.permissions);
            break;
          case 'NotFoundError':
          case 'OverconstrainedError':
          case 'TypeError':
            setErrorType(CameraErrorTypes.device_not_found);
            break;
          case 'NotReadableError':
          case 'InvalidStateError':
            setErrorType(CameraErrorTypes.device_in_use);
            break;
          default:
            setErrorType(CameraErrorTypes.failed_to_access_the_device);
            break;
        }
      }
    }
  }, [stopCamera, aspectRatio]);

  useEffect(() => {
    if (isOn) {
      startCamera();
    }

    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera, isOn]);

  const guide = useMemo(() => {
    if (errorType === CameraErrorTypes.none) {
      return <></>;
    }

    const getPermissionsGuideKey = () => {
      let permissionsGuideKey: string = 'default';
      const browserData = commonUtils.getBrowserData();

      if (browserData.isIOS) {
        permissionsGuideKey = 'ios';
      } else if (browserData.isAndroid) {
        permissionsGuideKey = 'android';
      } else if (browserData.isChrome) {
        permissionsGuideKey = 'chrome';
      } else if (browserData.isFirefox) {
        permissionsGuideKey = 'firefox';
      } else if (browserData.isEdge) {
        permissionsGuideKey = 'edge';
      } else if (browserData.isSafari) {
        permissionsGuideKey = 'safari';
      }

      return `CameraErrorPopup.InfoGuide.${permissionsGuideKey}`;
    };

    const guideKey: string =
      errorType === CameraErrorTypes.permissions
        ? getPermissionsGuideKey()
        : `CameraErrorPopup.InfoErrors.${errorType}`;

    return (
      <div
        className={classNames.guide}
        dangerouslySetInnerHTML={{ __html: t(guideKey).toString() }}
      />
    );
  }, [t, errorType]);

  const cameraErrorPopup = (
    <Transition
      in={showPermissionPopup}
      timeout={400}
      mountOnEnter
      unmountOnExit
      children={state => {
        return (
          <>
            <AppBackdrop
              className={classes(classNames.cameraErrorPopupBackdrop, `backdrop-${state}`)}
            />
            <AppModal className={classes(classNames.cameraErrorPopupModal, `fade-${state}`)}>
              <div className={classNames.cameraErrorPopupContent}>
                <div className={classNames.cameraErrorPopupIcon}>
                  <SVG src={cameraIcon} />
                </div>
                <div className={classNames.cameraErrorPopupBody}>
                  <div className={classNames.cameraErrorPopupBodyTitle}>
                    {t('CameraErrorPopup.title')}
                  </div>
                  <div className={classNames.cameraErrorPopupBodyContent}>
                    {t('CameraErrorPopup.description')}
                  </div>
                </div>

                {guide}

                <div className={classNames.cameraErrorPopupFooter}>
                  <TheButton
                    className={classNames.cameraErrorPopupApproveCameraBtn}
                    text={t('CameraErrorPopup.resolvedBtn')}
                    shouldRecolor={false}
                    emitOnClick={startCamera}
                  />
                  <TheButton
                    className={classNames.cameraErrorPopupContinueBtn}
                    text={t('CameraErrorPopup.skipBtn')}
                    plain={true}
                    shouldRecolor={false}
                    emitOnClick={() => {
                      toggleCamera?.();
                      setShowPermissionPopup(false);
                    }}
                  />
                </div>
              </div>
            </AppModal>
          </>
        );
      }}
    />
  );

  return (
    <>
      <div
        className={classes(classNames.camera, {
          [className || '']: className,
          [classNames.cameraOff]: !isOn || !permissionGranted,
        })}
        {...props}
      >
        <video ref={videoRef} autoPlay playsInline muted />
        {permissionGranted === false && (
          <div
            className={classes(classNames.permission, {
              [permissionClassName || '']: permissionClassName,
            })}
          >
            <p>{t('cameraPermission.text')}</p>
            <button onClick={startCamera}>{t('cameraPermission.button')}</button>
          </div>
        )}
        {(!isOn || !permissionGranted) && (
          <CameraController layout='camera' off={!isOn} onClick={toggleCamera} />
        )}
      </div>
      {cameraErrorPopup}
    </>
  );
};

export default Camera;
